if (!customElements.get("product-info")) {
  class ProductInfo extends HTMLElement {
    productURL;
    abortController;

    constructor() {
      super();
      this.productURL = this.getAttribute("data-url");

      document.addEventListener("variantChange", (e) => {
        const variantId = e.detail.variantId;
        const variantTitle = e.detail.variantTitle;
        const variantAvailable = e.detail.variantAvailable;
        this.handleFetchVariant({ variantId, variantTitle, variantAvailable });
        this.updateURLHash({ variantId, variantTitle });
      });

      document.addEventListener("categorySizeChange", (e) => {
        const categorySize = e.detail.categorySize;
        this.updateURLHash({ categorySize });
      });

      if (this.currentVariant) {
        this.handleUpdateProductAvailability({
          variantId: this.currentVariant?.id,
          variantTitle: this.currentVariant?.title,
          variantAvailable: this.currentVariant?.available,
        });
      }
    }

    handleUpdateLineItemProperties(variantTitle) {
      if (this.querySelector('[name="properties[Selected size]"]')) {
        this.querySelector('[name="properties[Selected size]"]').setAttribute(
          "value",
          variantTitle,
        );
      }
    }

    handleUpdateProductStock(html) {
      if (!this.querySelector(".product__size-stock")) return;
      this.querySelector(".product__size-stock").classList.toggle(
        "last",
        html.querySelector(".product__size-stock").classList.contains("last"),
      );
      this.querySelector(".product__size-stock").innerHTML = html.querySelector(
        ".product__size-stock",
      ).innerHTML;
    }

    handleSetMultiLocationStatus = (availability) => {
      const availabilityContainer = this.querySelector(".product__size-availability");
      if (!availabilityContainer) return;

      // Hide sold out message, show location items
      const soldOutEl = availabilityContainer.querySelector(".product__size-availability-sold-out");
      const locationItems = availabilityContainer.querySelectorAll(".product__size-availability-item");
      
      if (soldOutEl) soldOutEl.style.display = "none";
      locationItems.forEach(item => item.style.display = "");

      // Update online status (special case, not in availabilityLocations)
      if (availability.online !== undefined) {
        const onlineDot = availabilityContainer.querySelector('[data-location="online"] .status-dot');
        if (onlineDot) {
          onlineDot.classList.toggle("status-dot--available", availability.online);
          onlineDot.classList.toggle("status-dot--unavailable", !availability.online);
        }
      }

      // Dynamically update all locations from availabilityLocations config
      availabilityLocations.forEach((location) => {
        // Match location name (case-insensitive) to data-location attribute
        const locationName = location.name.toLowerCase();
        const locationItem = availabilityContainer.querySelector(`[data-location="${locationName}"]`);
        const locationDot = locationItem?.querySelector('.status-dot');
        
        if (locationItem && locationDot && availability[location.name] !== undefined) {
          const isAvailable = availability[location.name];
          locationDot.classList.toggle("status-dot--available", isAvailable);
          locationDot.classList.toggle("status-dot--unavailable", !isAvailable);
        }
      });
    };

    handleUpdateProductAvailability({
      variantId,
      variantTitle,
      variantAvailable,
    }) {
      const availabilityProductId = Shopify.product.id;

      // If variant is not available at all, show sold out
      if (variantAvailable === "false" || variantAvailable === false) {
        const availabilityContainer = this.querySelector(".product__size-availability");
        if (availabilityContainer) {
          const soldOutEl = availabilityContainer.querySelector(".product__size-availability-sold-out");
          const locationItems = availabilityContainer.querySelectorAll(".product__size-availability-item");
          
          if (soldOutEl) soldOutEl.style.display = "";
          locationItems.forEach(item => item.style.display = "none");
        }
        return;
      }

      // Check if availabilityLocations is defined
      if (typeof availabilityLocations === 'undefined' || !Array.isArray(availabilityLocations) || availabilityLocations.length === 0) {
        console.error('availabilityLocations is not defined or empty');
        return;
      }

      // Build location_ids parameter (comma-separated)
      const locationIdsParam = availabilityLocations.map(loc => loc.id).join(',');

      fetch(
        `${internalAPI}/inventory/?product_id=${availabilityProductId}&location_id=${locationIdsParam}`,
      )
        .then((response) => response.json())
        .then((data) => {
          // Ensure data is an array
          const inventoryData = Array.isArray(data) ? data : [];
          
          // Filter inventory data for this variant
          const variantInventory = inventoryData.filter(
            (item) => item && item.variant_id && item.variant_id.toString() === variantId.toString(),
          );

          // Build availability object dynamically from availabilityLocations config
          const availability = {
            // Online is available if variant is not explicitly false
            online: variantAvailable !== "false" && variantAvailable !== false
          };

          // Dynamically determine availability for each location in config
          availabilityLocations.forEach((location) => {
            if (!location || !location.id || !location.name) {
              console.warn('Invalid location in availabilityLocations:', location);
              return;
            }

            const locationInventory = variantInventory.find(
              (item) => item && item.location_id && item.location_id.toString() === location.id.toString(),
            );
            // Use location name as key for easy matching in handleSetMultiLocationStatus
            availability[location.name] = locationInventory ? locationInventory.available : false;
          });

          // Update display with multi-location status
          this.handleSetMultiLocationStatus(availability);
        })
        .catch((error) => {
          console.error('Error fetching inventory data:', error);
        });
    }

    handleAddingProductToCart(adding = false) {
      const ELM_productForm = this.querySelector(".product__form");
      ELM_productForm.classList.toggle("adding", adding);
    }

    handleFetchVariant({ variantId, variantTitle, variantAvailable }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      const url = this.buildFetchUrl(variantId);
      this.handleAddingProductToCart(true);
      fetch(url, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((response) => {
          const html = new DOMParser().parseFromString(response, "text/html");

          this.updateCurrentVariant(html);
          this.updateProductPrice(html);
          this.updateBuyButton(html);
          this.updateVariantIdInput(html);
          this.handleUpdateLineItemProperties(variantTitle);
          this.handleUpdateProductStock(html);
          this.handleUpdateProductAvailability({
            variantId,
            variantTitle,
            variantAvailable,
          });
        })
        .finally(() => {
          this.handleAddingProductToCart(false);
        });
    }

    updateVariantIdInput(html) {
      this.querySelector('[name="id"]').setAttribute(
        "value",
        html.querySelector('[name="id"]').getAttribute("value"),
      );
    }

    updateProductPrice(html) {
      this.querySelector(".product__total-price").innerHTML =
        html.querySelector(".product__total-price").innerHTML;
    }

    updateBuyButton(html) {
      if (!this.querySelector('.product__form input[type="submit"]')) return;
      ["class", "value"].forEach((attr) => {
        this.querySelector('.product__form input[type="submit"]').setAttribute(
          attr,
          html
            .querySelector('.product__form input[type="submit"]')
            .getAttribute(attr),
        );
      });

      const newDisabled = html
        .querySelector('.product__form input[type="submit"]')
        .getAttribute("disabled");

      this.querySelector('.product__form input[type="submit"]').toggleAttribute(
        "disabled",
        newDisabled !== null,
      );
    }

    get currentVariant() {
      const selectedVariant = this.querySelector(
        "[data-selected-variant]",
      )?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    updateCurrentVariant(html) {
      this.querySelector("[data-selected-variant]").innerHTML =
        html.querySelector("[data-selected-variant]").innerHTML;
    }

    buildFetchUrl(variantId) {
      if (!variantId) return this.productURL;
      return `${this.productURL}?variant=${variantId}`;
    }

    updateURLHash({ variantId, variantTitle, categorySize }) {
      const url = this.productURL;
      const urlSearchParams = new URLSearchParams(window.location.search);

      if (
        urlSearchParams.has("variant") ||
        urlSearchParams.has("selected_variant_title") ||
        urlSearchParams.has("category_size")
      ) {
        if (variantId) {
          urlSearchParams.set("variant", variantId);
        }
        if (variantTitle) {
          urlSearchParams.set("selected_variant_title", variantTitle);
        }
        if (categorySize) {
          urlSearchParams.set("category_size", categorySize);
        }
        window.history.replaceState(
          {},
          "",
          `${url}?${urlSearchParams.toString()}`,
        );
      } else {
        let updatedURL = url;
        const urlSearchParams = new URLSearchParams(url.split("?")[1]);
        if (variantId) {
          urlSearchParams.set("variant", variantId);
        }
        if (variantTitle) {
          urlSearchParams.set("selected_variant_title", variantTitle);
        }
        if (categorySize) {
          urlSearchParams.set("category_size", categorySize);
        }
        window.history.replaceState(
          {},
          "",
          `${updatedURL}?${urlSearchParams.toString()}`,
        );
      }
    }
  }
  customElements.define("product-info", ProductInfo);
}
